var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal, ModalFooter, Button, TextField, Checkbox, Row, Col, } from "@folio/stripes/components";
import { createSetPasswordSchema, } from "../utils/validationUtils";
import { PASSWORD_INPUT_TYPE, NEW_PASSWORD_AUTOCOMPLETE, asTextFieldInputRef, } from "../utils/passwordInputProps";
import PasswordRulesHint from "./PasswordRulesHint";
var PASSWORD_RULES_HINT_ID = "set-password-rules";
var SetPasswordModal = function (_a) {
    var open = _a.open, onClose = _a.onClose, onSubmit = _a.onSubmit, _b = _a.isSubmitting, isSubmitting = _b === void 0 ? false : _b;
    var intl = useIntl();
    var schema = useMemo(function () { return createSetPasswordSchema(intl); }, [intl]);
    // The field ref goes to stripes TextField as `inputRef`, never as `ref` (CZNA-1591):
    // TextField is a class component, so a `ref` would be its instance, which has no
    // `focus()`. react-hook-form focuses the first invalid field on a failed submit; with the
    // instance that call threw and the per-rule message never rendered. With the <input> the
    // focus lands on the field that needs fixing, so focusing stays on.
    var _c = useForm({
        resolver: zodResolver(schema),
        defaultValues: { password: "", passwordConfirm: "", temporary: true },
    }), handleSubmit = _c.handleSubmit, control = _c.control, reset = _c.reset, errors = _c.formState.errors;
    var handleClose = function () {
        reset();
        onClose();
    };
    var handleFormSubmit = function (values) {
        onSubmit(values.password, values.temporary);
    };
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit(handleFormSubmit), disabled: isSubmitting },
            React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.setPassword.submit" })),
        React.createElement(Button, { onClick: handleClose },
            React.createElement(FormattedMessage, { id: "ui-czna-users.cancel" }))));
    return (React.createElement(Modal, { open: open, label: intl.formatMessage({
            id: "ui-czna-users.users.action.setPassword.title",
        }), onClose: handleClose, dismissible: true, footer: footer },
        React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit) },
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(Controller, { control: control, name: "password", render: function (_a) {
                            var _b;
                            var _c = _a.field, ref = _c.ref, field = __rest(_c, ["ref"]);
                            return (React.createElement(TextField, __assign({}, field, { inputRef: asTextFieldInputRef(ref), type: PASSWORD_INPUT_TYPE, autoComplete: NEW_PASSWORD_AUTOCOMPLETE, label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.password" }), error: (_b = errors.password) === null || _b === void 0 ? void 0 : _b.message, "aria-describedby": PASSWORD_RULES_HINT_ID, required: true, autoFocus: true })));
                        } }),
                    React.createElement(PasswordRulesHint, { id: PASSWORD_RULES_HINT_ID }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(Controller, { control: control, name: "passwordConfirm", render: function (_a) {
                            var _b;
                            var _c = _a.field, ref = _c.ref, field = __rest(_c, ["ref"]);
                            return (React.createElement(TextField, __assign({}, field, { inputRef: asTextFieldInputRef(ref), type: PASSWORD_INPUT_TYPE, autoComplete: NEW_PASSWORD_AUTOCOMPLETE, label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.passwordConfirm" }), error: (_b = errors.passwordConfirm) === null || _b === void 0 ? void 0 : _b.message, required: true })));
                        } }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(Controller, { control: control, name: "temporary", render: function (_a) {
                            var _b = _a.field, value = _b.value, onChange = _b.onChange, field = __rest(_b, ["value", "onChange"]);
                            return (React.createElement(Checkbox, __assign({}, field, { checked: value, onChange: function (e) {
                                    return onChange(e.target.checked);
                                }, label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.password.temporary" }) })));
                        } }))))));
};
export default SetPasswordModal;
